/**
 * The system check's evidence from an event's `evidence` JSON (ADR D5): E1 an ERP report that
 * came first, E2 an earlier payment a statement confirmed, E3 the VAT register's answer.
 */
import { Temporal, plainDateFrom } from '@insio/dates';
import { SystemCheckRule } from '@insio/shared-types';

export type SystemCheckEvidence =
  | {
      rule: typeof SystemCheckRule.E1;
      /** The connector whose ERP report came first; null when it cannot be named. */
      connectorName: string | null;
    }
  // E2's ids (raw UUIDs) stay unread until a payment or movement can be linked to.
  | { rule: typeof SystemCheckRule.E2 }
  | {
      rule: typeof SystemCheckRule.E3;
      /** The register asked: ADIS (CZ) or ODFS (SK). */
      register: string | null;
      vatId: string | null;
      companyId: string | null;
      /** The IČO the SK register names for the VAT ID; null for CZ. */
      registerCompanyId: string | null;
      /** The payer's name as the register gives it; null when it could not be read. */
      registerName: string | null;
      /** The day the register was asked; null when it cannot be read. */
      checkedOn: Temporal.PlainDate | null;
    };

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function plainDateOrNull(value: unknown): Temporal.PlainDate | null {
  const text = stringOrNull(value);
  if (!text) return null;
  try {
    return plainDateFrom(text);
  } catch {
    return null;
  }
}

/** The evidence of a SYSTEM_CHECKED event; null when it is missing or of an unknown rule. */
export function parseSystemCheckEvidence(value: unknown): SystemCheckEvidence | null {
  if (typeof value !== 'object' || value === null) return null;
  const evidence = value as Record<string, unknown>;
  if (evidence.rule === SystemCheckRule.E1) {
    return { rule: SystemCheckRule.E1, connectorName: stringOrNull(evidence.connectorName) };
  }
  if (evidence.rule === SystemCheckRule.E2) {
    return { rule: SystemCheckRule.E2 };
  }
  if (evidence.rule === SystemCheckRule.E3) {
    return {
      rule: SystemCheckRule.E3,
      register: stringOrNull(evidence.register),
      vatId: stringOrNull(evidence.vatId),
      companyId: stringOrNull(evidence.companyId),
      registerCompanyId: stringOrNull(evidence.registerCompanyId),
      registerName: stringOrNull(evidence.registerName),
      checkedOn: plainDateOrNull(evidence.checkedOn)
    };
  }
  return null;
}

/** A name without case, diacritics, white space, dots or commas: `Účetní s. r. o.` is `ucetnisro`. */
function comparableName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLocaleLowerCase('cs')
    .replace(/[\s.,]+/g, '');
}

/** Whether the register names another company than the partner; never when either is unknown. */
export function isRegisterNameDifferent(
  registerName: string | null,
  partyName: string | null | undefined
): boolean {
  if (!registerName || !partyName) return false;
  return comparableName(registerName) !== comparableName(partyName);
}
